import React, { useEffect, useRef, useState } from 'react';
import { promocodeService } from 'services/promocode.service';
import { Paginator } from 'primereact/paginator';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import { Dialog } from 'primereact/dialog';
import { Card } from 'primereact/card';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { routes } from 'utils/routes';
import { formatDate, getSortIcon, handleFilters, showTostify } from 'utils/common-functions';
import CustomToast from 'views/components/form-inputs/alert';
import { Skeleton } from 'primereact/skeleton';
import { _Object, getStatusLabel } from 'utils/interfaces';
import { InputField, RadioButton } from 'views/components/form-inputs';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';

const PromocodeLisiting = () => {
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<_Object>({})
  const [promocodes, setPromocodes] = useState([]);
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();
  const toastRef = useRef<any>(null);
  const [filterData, setFilterData] = useState<_Object>({
    page: 1,
    per_page: 10,
    q: "",
    sort: '-created_at',
  })
  const fetchPromocodes = async () => {
    try {
      setLoading(true);
      const data = await promocodeService.getPromocodes(filterData);
      setPromocodes(data.items);
      setPagination(data.pagination);
    } catch (error) {
      console.error('Error fetching promocodes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromocodes();
  }, [filterData]);

  const formik: any = useFormik({
    initialValues: {
      count: '',
      promocode_type: '',
    },
    validationSchema: Yup.object({
      count: Yup.string().required('Count is required'),
      promocode_type: Yup.string().label('Promocode Type').required(),
    }),
    onSubmit: async (values: any) => {
      setLoading(true);
      try {
        // delete values.type
        const data = await promocodeService.generatePromocodes(values);
        if (data.error === false) {
          formik.resetForm();
          showTostify(toastRef, 'success', data.message);
          fetchPromocodes();
          setVisible(false);
        } else {
          showTostify(toastRef, 'error', data.message);
        }
      } catch (err) {
        console.error('Error generating promocodes:', err);
      } finally {
        setLoading(false);
      }
    },
  });

  const onPageChange = (event: any) => {
    const currentPage = Math.floor(event.first / event.rows) + 1;

    setFilterData((prev) => ({
      ...prev,
      page: currentPage,
      per_page: event.rows,
    }));
  };

  const formikFilter = useFormik({
    initialValues: filterData,
    enableReinitialize: true,
    onSubmit: (values: _Object) => {
      setFilterData((prev) => ({
        ...prev,
        q: values.q.trim(),
        page: 1
      }));
    }
  });

  return (
    <div className="PromocodeLisiting">
      <CustomToast ref={toastRef} />
      <div className="flex justify-content-between align-items-center">
        <div className="p-inputgroup search-bar col-6">
          <form onSubmit={formikFilter.handleSubmit}>
            <div className='form-group mb-0 mr-2'>
              <Button type='submit' className='search-loading bg-transparent p-0 border-none'
                disabled={loading}>
                {loading && <i className="pi pi-spin pi-spinner"
                  style={{ fontSize: '1rem' }}
                >
                </i>}
              </Button>

              <IconField iconPosition="left">
                <InputIcon className="pi pi-search"> </InputIcon>
                <InputText placeholder="Search"
                  name='q'
                  onChange={formikFilter.handleChange}
                  onBlur={formikFilter.handleBlur}
                  value={formikFilter.values.q} />
              </IconField>

              {filterData.q && <Button type='button'
                onClick={() => {
                  setFilterData((prev) => ({
                    ...prev,
                    q: '',
                    page: 1
                  }))
                }}
                severity="secondary" text className='bg-transparent p-0 border-none absolute top-0 right-0 mr-3'>
                <span className="pi pi-times "> </span>
              </Button>}
            </div>
          </form>

          {pagination?.total_items > 0 &&
            <div className='total-items flex align-items-center text-sm'>
              Total items: {pagination.total_items || ""}
            </div>
          }
        </div>


        <div className='col justify-content-end flex gap-2'>
          <Button label="Create Promocodes" icon="pi pi-external-link" className="primary" onClick={() => setVisible(true)} />
          <Button label="Excel" className="primary" onClick={() => navigate(routes.excel)} />
        </div>
      </div>

      <Dialog header="Generate Promocode" visible={visible} modal onHide={() => setVisible(false)}>
        <Card className='check-deals-code shadow-none border-primary-color'>
          <form onSubmit={formik.handleSubmit}>
            <RadioButton
              className="custom-form-check"
              name="promocode_type"
              value={formik.values.promocode_type || ""}
              onChange={(e: any) => formik.setFieldValue('promocode_type', e.value)}
              onBlur={formik.handleBlur}
              options={[
                { label: 'Normal', value: 'normal' },
                { label: 'Silver', value: 'silver' },
                { label: 'Gold', value: 'gold' },
              ]}
              required={true}
              label="Promocode type"
              error={formik.touched.promocode_type && formik.errors.promocode_type}
            />
            <InputField
              className="mb-3"
              name="count"
              type="number"
              label="Count"
              required={true}
              value={formik.values.count}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik?.touched.count && formik.errors.count}
            />
            <Button loading={loading} label="Generate" className='primary' type="submit" />
          </form>
        </Card>
      </Dialog>

      <div className="col">
        <div className="card">
          <table className='w-full'>
            <thead>
              <tr>
                <th>#</th>
                <th>Promocode</th>
                <th>Type</th>
                <th>
                  <button
                    className="btn p-0 border-0"
                    onClick={() => { handleFilters('sort', filterData.sort === '-created_at' ? 'created_at' : '-created_at', setFilterData) }}>
                    Start Date
                    <i className={`pi ${getSortIcon(filterData.sort.includes('created_at') ? filterData.sort : '')}`}></i>
                  </button>
                </th>
                <th>Excel number</th>
                <th>Service ID</th>
                <th>Customer ID</th>
                <th>Total Amount</th>
                <th>Amount Paid</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {loading && Array.from({ length: 5 }).map((_, index) => (
                <tr key={index}>
                  <td><Skeleton height="2rem" borderRadius="1px" /></td>
                  <td><Skeleton height="2rem" borderRadius="4px" /></td>
                  <td><Skeleton height="2rem" borderRadius="4px" /></td>
                  <td><Skeleton height="2rem" borderRadius="4px" /></td>
                  <td><Skeleton height="2rem" borderRadius="4px" /></td>
                  <td><Skeleton height="2rem" borderRadius="4px" /></td>
                  <td><Skeleton height="2rem" borderRadius="4px" /></td>
                  <td><Skeleton height="2rem" borderRadius="4px" /></td>
                  <td><Skeleton height="2rem" borderRadius="4px" /></td>
                  <td><Skeleton height="2rem" borderRadius="4px" /></td>
                </tr>
              ))}
              {!loading && promocodes?.map((item: _Object, i: number) => (
                <tr>
                  <td>{((filterData.page - 1) * filterData.per_page) + (i + 1)}</td>
                  <td> {item?.promocode || "-"}</td>
                  <td>{item.type || '-'}</td>
                  <td>{formatDate(item.created_at) || '-'}</td>
                  <td>{item?.excel_number || '-'}</td>
                  <td>{item?.service_id || '-'}</td>
                  <td>{item?.customer_id || '-'}</td>
                  <td>{item?.total_amount || '-'}</td>
                  <td>{item?.paid_amount || '-'}</td>
                  <td>{getStatusLabel(item?.status) || "-"}
                    {/* <InputSwitch
                      checked={item.status === 'active'}
                      className={`${item.status === 'active' ? "switcher" : "inactive-switcher"}`}
                      onChange={async (e) => {
                        const newStatus = e.value ? 'active' : 'redeemed';
                        setLoading(true);
                        try {
                          const data = await promocodeService.updatePromoCode(item.id, {
                            promocode: item.promocode,
                            status: newStatus,
                          });
                          if (data.error === false) {
                            showTostify(toastRef, 'success', data.message);
                            setPromocodes((prevPromocodes: any) =>
                              prevPromocodes.map((promo: any) =>
                                promo.id === item.id ? { ...promo, status: newStatus } : promo
                              )
                            );
                          } else {
                            showTostify(toastRef, 'error', data.message);
                          }
                        } catch (error) {
                          console.error('Error changing status:', error);
                          showTostify(toastRef, 'error', 'An error occurred while changing the status.');
                        } finally {
                          setLoading(false);
                        }
                      }}
                    /> */}
                  </td>
                </tr>
              ))}
              {!loading && promocodes?.length === 0 && <tr>
                <td colSpan={11}>No available options</td>
              </tr>
              }
            </tbody>
          </table>
        </div>

        {!loading && pagination.total_pages > 1 && (
          <Paginator
            first={(filterData.page - 1) * filterData.per_page}
            rows={filterData.per_page}
            totalRecords={pagination.total_items}
            rowsPerPageOptions={[10, 20, 30]}
            onPageChange={onPageChange}
          />
        )}

      </div>
    </div >
  );
};

export default PromocodeLisiting;
